import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Checkbox,
  CheckboxGroup,
  Box,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  SimpleGrid,
} from '@chakra-ui/react';
import rolePermissionService from 'services/rolePermissionService';
import authService from 'services/authService';

export default function PermissionsModal({ isOpen, onClose, role }) {
  const [permissions, setPermissions] = useState([]);
  const [, setRolePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const handlePermissionChange = (permissionIds) => {
    console.log('=== PERMISSION CHANGE ===');
    console.log('New selected permissions:', permissionIds);
    setSelectedPermissions(permissionIds);
  };

  const handleIndividualPermissionChange = (permissionId, isChecked) => {
    console.log('=== INDIVIDUAL PERMISSION CHANGE ===');
    console.log('Permission ID:', permissionId);
    console.log('Is checked:', isChecked);
    
    const permissionIdStr = String(permissionId);
    
    setSelectedPermissions(prev => {
      let newPermissions;
      if (isChecked) {
        newPermissions = [...prev, permissionIdStr];
      } else {
        newPermissions = prev.filter(id => id !== permissionIdStr);
      }
      console.log('Updated permissions:', newPermissions);
      return newPermissions;
    });
  };

  const canAssignPermissions = authService.hasPermission('permissions.assign');
  
  console.log('=== PERMISSIONS MODAL DEBUG ===');
  console.log('canAssignPermissions:', canAssignPermissions);
  console.log('user permissions:', authService.getCurrentUserPermissions());
  console.log('user role:', authService.getUserRole());

  useEffect(() => {
    if (isOpen && role) {
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, role]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      console.log('=== LOADING PERMISSIONS DATA ===');
      console.log('Role:', role);
      
      // Cargar todos los permisos
      const permissionsResponse = await rolePermissionService.getPermissions();
      const allPermissions = permissionsResponse.payload?.permissions || [];
      
      console.log('All permissions loaded:', allPermissions.length);
      console.log('First few permissions:', allPermissions.slice(0, 3));
      
      // Cargar permisos del rol
      const rolePermsResponse = await rolePermissionService.getRolePermissions(role.id);
      const currentRolePermissions = rolePermsResponse.payload?.permissions || [];
      
      console.log('Role permissions loaded:', currentRolePermissions.length);
      console.log('Role permissions:', currentRolePermissions);
      
      setPermissions(allPermissions);
      setRolePermissions(currentRolePermissions);
      // Convertir IDs a strings para que funcione con CheckboxGroup
      setSelectedPermissions(currentRolePermissions.map(p => String(p.id)));
      
    } catch (error) {
      console.error('Error loading permissions:', error);
      toast({
        title: 'Error',
        description: 'Error al cargar los permisos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      
      console.log('=== SAVING PERMISSIONS ===');
      console.log('Role ID:', role.id);
      console.log('Selected permissions (strings):', selectedPermissions);
      
      // Convertir de vuelta a números para el API
      const permissionIds = selectedPermissions.map(id => parseInt(id, 10));
      console.log('Selected permissions (numbers):', permissionIds);
      
      await rolePermissionService.assignPermissionsToRole(role.id, permissionIds);
      
      toast({
        title: 'Éxito',
        description: 'Permisos actualizados correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onClose();
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast({
        title: 'Error',
        description: 'Error al guardar los permisos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Agrupar permisos por recurso
  const groupedPermissions = permissions.reduce((groups, permission) => {
    const resource = permission.resource;
    if (!groups[resource]) {
      groups[resource] = [];
    }
    groups[resource].push(permission);
    return groups;
  }, {});

  const resourceLabels = {
    users: 'Usuarios',
    roles: 'Roles',
    clients: 'Clientes',
    vehicles: 'Vehículos',
    services: 'Servicios',
    schedule: 'Agenda',
    reports: 'Reportes',
    inventory: 'Inventario',
    settings: 'Configuración',
    invoices: 'Facturas',
    providers: 'Proveedores',
    products: 'Productos'
  };

  const actionLabels = {
    view: 'Ver',
    create: 'Crear',
    edit: 'Editar',
    delete: 'Eliminar',
    assign: 'Asignar',
    view_own: 'Ver Propio',
    generate: 'Generar',
    manage: 'Gestionar'
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="800px">
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <Text>Permisos del Rol: {role?.name}</Text>
            <Text fontSize="sm" color="gray.500">
              {role?.description || 'Gestiona los permisos asignados a este rol'}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody maxH="500px" overflowY="auto">
          {loading ? (
            <Text>Cargando permisos...</Text>
          ) : (
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" align="center">
                <Text fontSize="sm" color="gray.600">
                  Selecciona los permisos que tendrá este rol
                </Text>
                <Badge colorScheme="gray">
                  {selectedPermissions.length} de {permissions.length} seleccionados
                </Badge>
              </HStack>

              <CheckboxGroup
                value={selectedPermissions}
                onChange={handlePermissionChange}
              >
                <Accordion allowMultiple defaultIndex={[0, 1, 2]}>
                  {Object.entries(groupedPermissions).map(([resource, resourcePermissions]) => (
                    <AccordionItem key={resource}>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <HStack>
                              <Text fontWeight="semibold">
                                {resourceLabels[resource] || resource}
                              </Text>
                              <Badge size="sm" colorScheme="gray">
                                {resourcePermissions.filter(p => 
                                  selectedPermissions.includes(String(p.id))
                                ).length} / {resourcePermissions.length}
                              </Badge>
                            </HStack>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        <SimpleGrid columns={2} spacing={3}>
                          {resourcePermissions.map((permission) => (
                            <Checkbox
                              key={permission.id}
                              value={String(permission.id)}
                              isChecked={selectedPermissions.includes(String(permission.id))}
                              onChange={(e) => handleIndividualPermissionChange(permission.id, e.target.checked)}
                              isDisabled={false}
                            >
                              <VStack align="start" spacing={0}>
                                <Text fontSize="sm" fontWeight="medium">
                                  {actionLabels[permission.action] || permission.action}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {permission.name}
                                </Text>
                              </VStack>
                            </Checkbox>
                          ))}
                        </SimpleGrid>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CheckboxGroup>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSavePermissions}
            isLoading={saving}
            loadingText="Guardando..."
            isDisabled={loading}
          >
            Guardar Permisos
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}